import { useEffect, useState } from "react";
import type { ExperimentConfig, OptimizelyEvent } from "stores/OptimizelyStore";
import { useOptimizelyStore } from "stores/OptimizelyStore";
import useExtendedRouter from "hooks/useExtendedRouter";
import { useOptimizelyExperiments } from "contexts/DataContext/hooks";
import type { OptimizelyTransformedExperiment } from "services/optimizely/types";
import {
  getExperimentStates,
  getIncludeDraftExperiments,
  getOptimizelyVariations,
  pushConversionEvent,
  setIncludeDraftExperiments,
} from "./utils";
import { useIsSupportedPage } from "./useOptimizelySnippet";

export const useOptimizely = (experimentConfig: ExperimentConfig) => {
  const experiments = useOptimizelyExperiments();
  const { isLoading, isLoaded, hasError } = useOptimizelyStore();
  const [variationIndex, setVariationIndex] = useState<number>(null);
  const [variationId, setVariationId] = useState<number>(null);
  const [forceDraftExperiments, setForceDraftExperiments] = useState<boolean>(false);
  const { asPath } = useExtendedRouter();
  const experiment = experiments.find((experiment) => experiment.id === experimentConfig.experimentId);
  const isSupportedPage = useIsSupportedPage(experiments, forceDraftExperiments);

  const setVariationData = (variationId: string) => {
    const index = experiment?.variations?.findIndex(
      (variationIndex) => variationIndex.variation_id === Number(variationId)
    );
    setVariationIndex(index === -1 ? null : index);
    setVariationId(Number(variationId));
  };

  const setOptimizelyVariation = (event: OptimizelyEvent, experiment: OptimizelyTransformedExperiment) => {
    if (hasError || !experiment || experiment?.variations?.length === 0) {
      return;
    }
    const { variationId } = event.data.decision;
    setVariationData(variationId);
  };

  useEffect(() => {
    if (!experiment) {
      return;
    }

    setIncludeDraftExperiments();
    if (getIncludeDraftExperiments()) setForceDraftExperiments(true);
    getOptimizelyVariations((event) => setOptimizelyVariation(event, experiment));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiment]);

  useEffect(() => {
    if (isLoaded && experiment.id) {
      const experimentStates = getExperimentStates();
      const variationId = experimentStates[experiment.id]?.variation?.id;

      setVariationData(variationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, asPath, experiment]);

  if (!isSupportedPage) return {};

  return {
    variationIndex: experiment ? variationIndex : null,
    variationId: experiment ? variationId : null,
    pushConversionEvent: pushConversionEvent(experimentConfig),
    isLoading,
    isLoaded,
  };
};
